import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import {
  Container,
  Columns,
  Column,
  Select,
  Checkbox,
  Button,
  ButtonGroup,
  Chip,
} from '@resurs/midas-react'

import { midas, variables } from '@resurs/midas'
import { Conditional } from '@resurs/react-utils'

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { TextInput } from '../../../../midasComponents'
import Page from '../../../../internal/Page'
import PageActionBar from '../../../../internal/PageActionBar'
import Datepicker from '../../../../internal/Datepicker'

import { getText } from '../../../../internal/TextLabels'
import { format } from '../../../../models/format'
import { Roles } from '../../../../models/roles'
import { shouldUsernameEqualEmail, validateUsername } from '../../utils'
import PermissionPicker from '../../UI/PermissionPicker'
import Banner from '../../../../components/CommonUI/Banner/Banner'

const UserDetailsUI = props => {
  const isIndefiniteDisabled = () => {
    if (
      moment(props.user.endDate, 'YYYY-MM-DD').isAfter('9999-12-01') ||
      moment(props.user.endDate, 'YYYYY-MM-DD').isAfter('9999-12-01')
    ) {
      return true
    }
    return false
  }

  const [user, setUser] = useState(props.user)
  const [disabled, setDisabled] = useState(true)
  const [indefiniteDisabled, setIndefiniteDisabled] = useState(
    isIndefiniteDisabled(),
  )
  const [currentRepresentative, setCurrentRepresentative] = useState(
    props.currentRepresentative,
  )
  const [addedStores, setAddedStores] = useState([])
  const [selectedStores, setSelectedStores] = useState(props.selectedStores)
  const [invalidFields, setInvalidFields] = useState([])

  const handleValidCallback = (field, isValidField) => {
    if (isValidField) {
      setInvalidFields([...invalidFields.filter(f => f !== field)])
    } else {
      const tmp = invalidFields
      tmp.push(field)
      setInvalidFields([...tmp.filter((v, i, a) => a.indexOf(v) === i)])
    }
  }

  useEffect(() => {
    setSelectedStores(props.selectedStores)
  }, [props.selectedStores])

  const representativeList = () => {
    const _reps = []
    props.representatives.map(rep => {
      _reps.push({
        key: rep.id,
        value: rep.id,
        valueLabel: `[${rep.country}] ${rep.accountSystemChainId}: ${rep.name}`,
      })
      return true
    })
    return _reps
  }

  const storesList = () => {
    const _stores = []
    if (props.stores && props.stores.length > 0) {
      props.stores
        .filter(store => {
          return (
            selectedStores &&
            selectedStores.filter(
              selectedStore => store.id === selectedStore.id,
            ).length === 0
          )
        })
        .filter(store => {
          return (
            addedStores &&
            addedStores.filter(addedStore => store.id === addedStore).length ===
              0
          )
        })
        .forEach(store => {
          _stores.push({
            key: store.id,
            value: store.id,
            valueLabel: `${store.name} - ${store.accountSystemStoreId}`,
          })
        })
    }
    return _stores
  }

  const handleUpdateState = (key, value) => {
    setUser({ ...user, [key]: value })
  }

  const handleChangeRepresentative = rep => {
    setCurrentRepresentative(rep)
    props.onChangeRepresentative(rep)
  }

  const handleAddStore = val => {
    const stores = addedStores
    const newStore =
      props.stores[props.stores.findIndex(store => store.id === val)]

    stores.push(newStore)

    const uniqueStores = Array.from(new Set(stores))

    setAddedStores(uniqueStores)
  }

  const handleRemoveAddedStore = (e, val) => {
    const removedAddedStores = addedStores.filter(store => store.id !== val.id)
    setAddedStores(removedAddedStores)
  }

  const handleRemoveSelectedStore = (e, val) => {
    const removeSelectedStores = selectedStores.filter(
      store => store.id !== val.id,
    )
    setSelectedStores(removeSelectedStores)
  }

  const handleChangePermission = ({
    role,
    additionalAuthorities,
    restrictedAuthorities,
  }) =>
    setUser({
      ...user,
      role: props.validRoles.find(({ name }) => name === role),
      additionalAuthorities,
      restrictedAuthorities,
    })

  const handleUpdateUser = () => {
    const newAddedStores = []
    addedStores.forEach(store => newAddedStores.push(store.id))

    const newSelectedStores = []
    selectedStores.forEach(store => newSelectedStores.push(store.id))

    const stores = newSelectedStores.concat(newAddedStores)

    const updatedUser = {
      ...user,
      stores,
      role: user.role.name,
    }
    props.onSave(updatedUser)
    setDisabled(true)
    setAddedStores([])
  }

  const { stores, representatives, loggedInUser } = props

  return (
    <form>
      <Container>
        <Columns>
          <Column col3 colXl2 colMd0 />
          <Column col6 colXl8 colMd12>
            {user?.temporaryEmail && (
              <Banner
                faIcon={faExclamationTriangle}
                type="warning"
                text={getText('user.temporaryEmail', user?.temporaryEmail)}
              />
            )}
            <Page
              title={`${getText('user.title')}: ${user.firstName} ${
                user.lastName
              }`}
            >
              <PageActionBar
                showPrev
                actions={
                  <ButtonGroup>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => props.onChangePassword()}
                    >
                      {getText('user.editPassword.reset')}
                    </Button>
                    {disabled ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => setDisabled(!disabled)}
                      >
                        {getText('user.edit.title')}
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        type="primary"
                        kind="success"
                        disabled={!!invalidFields.length}
                        onClick={() => handleUpdateUser()}
                      >
                        {getText('default.save')}
                      </Button>
                    )}
                  </ButtonGroup>
                }
              />
              <Container>
                <Columns>
                  <Column col12>
                    <TextInput
                      id="username"
                      label={getText('user.userName')}
                      value={user.username}
                      disabled={disabled}
                      required={getText('default.form.required')}
                      format={format.usernameOrEmail}
                      disableAutocomplete
                      additionalValidation={value =>
                        validateUsername(value, user.email)
                      }
                      onChange={e =>
                        handleUpdateState('username', e.target.value)
                      }
                      onValidChange={(field, isValidField) =>
                        handleValidCallback(field, isValidField)
                      }
                    />
                    <TextInput
                      id="first-name"
                      label={getText('user.firstName')}
                      value={user.firstName}
                      required={getText('default.form.required')}
                      format={format.string}
                      disabled={disabled}
                      disableAutocomplete
                      onChange={e =>
                        handleUpdateState('firstName', e.target.value)
                      }
                      onValidChange={(field, isValidField) =>
                        handleValidCallback(field, isValidField)
                      }
                    />
                    <TextInput
                      id="last-name"
                      label={getText('user.lastName')}
                      value={user.lastName}
                      required={getText('default.form.required')}
                      format={format.string}
                      disabled={disabled}
                      disableAutocomplete
                      onChange={e =>
                        handleUpdateState('lastName', e.target.value)
                      }
                      onValidChange={(field, isValidField) =>
                        handleValidCallback(field, isValidField)
                      }
                    />
                    <TextInput
                      id="email"
                      label={getText('user.email')}
                      value={user.email}
                      disabled={disabled}
                      format={format.email}
                      disableAutocomplete
                      required={
                        user.role.name === Roles.SUPER_USER
                          ? getText('default.form.required')
                          : ''
                      }
                      additionalValidation={value =>
                        shouldUsernameEqualEmail(user.username, value)
                          ? getText('user.add.emailMustMatchUsername')
                          : null
                      }
                      onChange={e => handleUpdateState('email', e.target.value)}
                      onValidChange={(field, isValidField) =>
                        handleValidCallback(field, isValidField)
                      }
                    />
                    <TextInput
                      id="phone"
                      label={getText('user.phone')}
                      value={user.phoneNumber}
                      disabled={disabled}
                      format={format.phone}
                      disableAutocomplete
                      required={
                        user.role.name === Roles.SUPER_USER
                          ? getText('default.form.required')
                          : ''
                      }
                      onChange={e =>
                        handleUpdateState('phoneNumber', e.target.value)
                      }
                      onValidChange={(field, isValidField) =>
                        handleValidCallback(field, isValidField)
                      }
                    />
                    <div
                      style={{
                        width: '100%',
                        display: 'inline-flex',
                        justifyContent: 'left',
                      }}
                    >
                      <Datepicker
                        name="start-date"
                        label={getText('user.startDate')}
                        selected={moment(user.startDate)}
                        disabled={disabled}
                        onChange={date =>
                          handleUpdateState(
                            'startDate',
                            moment(date).format('YYYY-MM-DD'),
                          )
                        }
                        maxDate={moment(user.endDate)}
                      />
                      <div style={{ paddingLeft: '1rem' }}>
                        {!indefiniteDisabled && (
                          <Datepicker
                            name="end-date"
                            label={getText('user.endDate')}
                            selected={
                              moment(user.endDate, 'YYYY-MM-DD').isAfter(
                                '9999-12-01',
                              )
                                ? moment()
                                : moment(user.endDate)
                            }
                            disabled={disabled}
                            onChange={date =>
                              handleUpdateState(
                                'endDate',
                                moment(date).format('YYYY-MM-DD'),
                              )
                            }
                            minDate={moment(user.startDate)}
                          />
                        )}
                      </div>
                      <div style={{ padding: '2.5rem 0 0 1rem' }}>
                        <Checkbox
                          disabled={disabled}
                          id="end-indefinite"
                          text={getText('user.edit.noEndDate')}
                          checked={indefiniteDisabled}
                          onChange={() => {
                            if (!indefiniteDisabled) {
                              handleUpdateState('endDate', '9999-12-31')
                            } else {
                              handleUpdateState(
                                'endDate',
                                moment().format('YYYY-MM-DD'),
                              )
                            }
                            setIndefiniteDisabled(!indefiniteDisabled)
                          }}
                        />
                      </div>
                    </div>
                  </Column>
                </Columns>
              </Container>
            </Page>
            <Page title={getText('user.rolesAndAuthorities')}>
              <Container>
                <Columns>
                  <Column col12>
                    <Conditional when={user.role.name !== Roles.SUPER_USER}>
                      <label className={midas.formLabel}>
                        {getText('user.stores')}
                      </label>
                      <div style={{ padding: '0.2rem 1rem 1rem 0rem' }}>
                        {selectedStores &&
                          selectedStores.map(store => (
                            <Chip
                              key={store.id}
                              disabled={disabled}
                              close={selectedStores.length > 1}
                              onClose={e => handleRemoveSelectedStore(e, store)}
                            >
                              {store.name}
                            </Chip>
                          ))}
                      </div>

                      {representatives.length > 1 && (
                        <div
                          style={{
                            background: variables.coreSecondaryColor,
                            padding: '0.2rem 1rem 1rem 1rem',
                          }}
                        >
                          <Select
                            id="all-reps"
                            disabled={disabled}
                            label={getText(
                              'store.select.representativeAndStore',
                            )}
                            value={currentRepresentative}
                            values={representativeList(representatives)}
                            onChange={e => handleChangeRepresentative(e)}
                          />
                        </div>
                      )}
                      {stores.length > 0 && (
                        <div
                          style={{
                            background: variables.coreSecondaryColor,
                            padding: '0.2rem 1rem 1rem 1rem',
                          }}
                        >
                          <Select
                            forceLabelAfterSelected
                            id="all-stores"
                            disabled={disabled}
                            label={getText('store.select.add.store')}
                            optionsLabel={getText('store.select.store')}
                            values={storesList(stores)}
                            onChange={val => handleAddStore(val)}
                          />
                          {addedStores &&
                            addedStores.map(store => (
                              <Chip
                                key={store.id}
                                disabled={disabled}
                                close={!disabled}
                                onClose={e => handleRemoveAddedStore(e, store)}
                              >
                                {store.name}
                              </Chip>
                            ))}
                        </div>
                      )}
                    </Conditional>

                    <PermissionPicker
                      user={user}
                      validRoles={props.validRoles}
                      loggedInUser={loggedInUser}
                      disabled={disabled}
                      onChange={handleChangePermission}
                    />
                  </Column>
                </Columns>
              </Container>
            </Page>
            <Container>
              <Columns justifyCenter>
                <Column col4>
                  <Button
                    type="primary"
                    kind="danger"
                    onClick={() => props.onDelete()}
                  >
                    {getText('user.delete.button')}
                  </Button>
                </Column>
              </Columns>
            </Container>
          </Column>
        </Columns>
      </Container>
    </form>
  )
}

UserDetailsUI.propTypes = {
  user: PropTypes.object,
  loggedInUser: PropTypes.object,
  validRoles: PropTypes.array,
  currentRepresentative: PropTypes.string.isRequired,
  representatives: PropTypes.array.isRequired,
  stores: PropTypes.array.isRequired,
  selectedStores: PropTypes.array,
  onChangePassword: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onChangeRepresentative: PropTypes.func.isRequired,
  onSave: PropTypes.func,
}

UserDetailsUI.defaultProps = {
  user: null,
  loggedInUser: null,
  validRoles: null,
  selectedStores: null,
  onSave: () => {},
}

export default UserDetailsUI
